/* ==========================================================================
   STYLES IN MOTION — facets.js
   <collection-facets>  filtering / sorting via Section Rendering + history
   <load-more>          progressive "Show more" (falls back to page links)
   <read-more>          clamped collection description disclosure
   Shared by main-collection, main-search and collection-hero.
   ========================================================================== */
const SIM = window.SIM || {};
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
const reveal = (root) => SIM.initReveal && SIM.initReveal(root);

const debounce = (fn, wait) => {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
};

/** Form → query string, dropping empty values (e.g. blank price inputs) */
const formQuery = (form) => {
  const params = new URLSearchParams();
  for (const [k, v] of new FormData(form)) if (String(v).trim() !== '') params.append(k, v);
  return params.toString();
};

/** Links fetched through section rendering may carry section_id — strip it */
const cleanLinks = (root) => {
  $$('a[href*="section_id="]', root).forEach((a) => {
    const u = new URL(a.getAttribute('href'), location.origin);
    u.searchParams.delete('section_id');
    a.setAttribute('href', u.pathname + u.search + u.hash);
  });
};

const sectionUrl = (query, sectionId) => {
  const u = new URL(location.pathname, location.origin);
  u.search = query;
  u.searchParams.set('section_id', sectionId);
  return u.toString();
};

const cache = new Map();
const fetchDoc = async (query, sectionId) => {
  const key = `${sectionId}?${query}`;
  if (!cache.has(key)) {
    const p = fetch(sectionUrl(query, sectionId)).then((r) => {
      if (!r.ok) throw new Error(r.status);
      return r.text();
    });
    cache.set(key, p);
    p.catch(() => cache.delete(key));
  }
  const doc = parse(await cache.get(key));
  cleanLinks(doc);
  return doc;
};

/* --------------------------------------------------------------------------
   <collection-facets>
   -------------------------------------------------------------------------- */
if (!customElements.get('collection-facets')) {
  class CollectionFacets extends HTMLElement {
    connectedCallback() {
      this.sid = this.dataset.sectionId;
      this.drawer = $(`#FacetsDrawer-${this.sid}`, this);
      this.query = location.search.slice(1);
      this.drawerDirty = false;

      this.onBarChange = debounce(() => this.applyFrom(this.barForm), 450);
      this.onDrawerChange = debounce(() => this.preview(), 350);

      this.addEventListener('change', (e) => this.handleChange(e));
      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-price-input]')) this.handleChange(e, 900);
      });
      this.addEventListener('submit', (e) => this.handleSubmit(e));
      this.addEventListener('click', (e) => this.handleClick(e));
      this.addEventListener('keydown', (e) => this.handleKey(e));
      this.addEventListener('toggle', (e) => this.handleToggle(e), true);
      this.addEventListener('focusout', (e) => this.handleFocusOut(e));
      this.onDocClick = (e) => { if (!e.target.closest('.facets__bar')) this.closeDropdowns(); };
      document.addEventListener('click', this.onDocClick);

      this.onPop = () => {
        const q = location.search.slice(1);
        if (q !== this.query) this.render(q, { push: false });
      };
      window.addEventListener('popstate', this.onPop);

      this.drawer?.addEventListener('dialog:close', () => {
        if (this.drawerDirty) this.resetDrawer();
      });

      this.initDensity();
      this.observeToolbar();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      window.removeEventListener('popstate', this.onPop);
      this.mq?.removeEventListener('change', this.onMq);
    }

    get barForm() { return $('[data-facets-form]', this); }
    get drawerForm() { return $('[data-facets-drawer-form]', this); }

    /* ---- events ---- */
    handleChange(e, wait) {
      const form = e.target.form || e.target.closest('form');
      if (!form) return;
      if (form === this.drawerForm) { this.drawerDirty = true; this.onDrawerChange(); return; }
      if (form !== this.barForm) return;
      if (e.target.matches('[data-facets-sort]')) { this.applyFrom(this.barForm); return; }
      if (wait) { clearTimeout(this.priceT); this.priceT = setTimeout(() => this.applyFrom(this.barForm), wait); return; }
      this.onBarChange();
    }

    handleSubmit(e) {
      const form = e.target;
      if (form === this.barForm) { e.preventDefault(); this.applyFrom(form); }
      if (form === this.drawerForm) {
        e.preventDefault();
        this.drawerDirty = false;
        SIM.closeDialog ? SIM.closeDialog(this.drawer) : this.drawer.close();
        this.render(formQuery(form), { scrollToResults: true });
      }
    }

    handleClick(e) {
      const more = e.target.closest('[data-facet-more]');
      if (more) {
        const facet = more.closest('.facet');
        const open = facet.classList.toggle('is-expanded');
        more.setAttribute('aria-expanded', String(open));
        more.textContent = open ? more.dataset.labelLess : more.dataset.labelMore;
        return;
      }
      const density = e.target.closest('[data-density-value]');
      if (density) { this.setDensity(density.dataset.densityValue, true); return; }

      const clear = e.target.closest('[data-facets-clear]');
      if (clear) {
        e.preventDefault();
        this.drawerDirty = true;
        this.preview(new URL(clear.href, location.origin).search.slice(1));
        return;
      }
      const link = e.target.closest('[data-facets-link]');
      if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
        e.preventDefault();
        const inChips = link.closest('.facets__active');
        this.render(new URL(link.href, location.origin).search.slice(1), { fromChip: !!inChips });
      }
    }

    handleKey(e) {
      if (e.key !== 'Escape') return;
      const open = $$('.facets__bar details[open]', this)[0];
      if (open) {
        e.preventDefault();
        open.open = false;
        $('summary', open).focus();
      }
    }

    handleToggle(e) {
      const d = e.target;
      if (!(d instanceof HTMLDetailsElement) || !d.open || !d.closest('.facets__bar')) return;
      $$('.facets__bar details[open]', this).forEach((o) => { if (o !== d) o.open = false; });
    }

    handleFocusOut(e) {
      const d = e.target.closest('.facets__bar details');
      if (!d) return;
      requestAnimationFrame(() => { if (!d.contains(document.activeElement)) d.open = false; });
    }

    closeDropdowns() { $$('.facets__bar details[open]', this).forEach((d) => (d.open = false)); }

    /* ---- rendering ---- */
    applyFrom(form) { this.render(formQuery(form)); }

    async render(query, { push = true, scrollToResults = false, fromChip = false } = {}) {
      this.query = query;
      const token = (this.token = Symbol('r'));
      const grid = $(`#ProductGrid-${this.sid}`, this);
      grid?.setAttribute('aria-busy', 'true');
      this.classList.add('is-loading');
      let doc;
      try {
        doc = await fetchDoc(query, this.sid);
      } catch {
        location.search = query; // hard fallback
        return;
      }
      if (token !== this.token) return;

      const state = this.captureState();
      this.swap(doc, [
        `#FacetsBar-${this.sid}`,
        `#FacetsActive-${this.sid}`,
        `#FacetsCount-${this.sid}`,
        `#FacetsBadge-${this.sid}`,
        `#FacetsDrawerBody-${this.sid}`,
        `#FacetsApply-${this.sid}`,
        `#ProductGrid-${this.sid}`,
      ]);
      $$('[data-facets-swap]').forEach((el) => {
        const next = el.id && doc.getElementById(el.id);
        if (next) el.innerHTML = next.innerHTML;
      });
      const nextBadge = doc.getElementById(`FacetsBadge-${this.sid}`);
      const badge = $(`#FacetsBadge-${this.sid}`, this);
      if (badge && nextBadge) badge.hidden = nextBadge.hidden;
      const sort = $('[data-facets-sort]', this);
      const nextSort = $('[data-facets-sort]', doc);
      if (sort && nextSort) sort.value = nextSort.value;
      this.drawerDirty = false;

      this.restoreState(state);
      this.classList.remove('is-loading');
      $(`#ProductGrid-${this.sid}`, this)?.removeAttribute('aria-busy');

      if (push) {
        const url = location.pathname + (query ? `?${query}` : '');
        history.pushState({ facets: true }, '', url);
      }
      this.announce();
      this.applySizes();
      reveal(this);

      // A removed chip took focus with it: move to the next chip, else the count
      if (fromChip && !this.contains(document.activeElement)) {
        const target = $('.facets__chip', this) || $(`#FacetsCount-${this.sid}`, this);
        target?.focus({ preventScroll: true });
      }
      if (scrollToResults) {
        const top = this.getBoundingClientRect().top + window.scrollY - 8;
        if (window.scrollY > top) window.scrollTo({ top, behavior: SIM.motionOK?.() ? 'smooth' : 'auto' });
      }
    }

    /** Drawer: refresh counts + "Show N results" without touching the grid */
    async preview(query = formQuery(this.drawerForm)) {
      const token = (this.previewToken = Symbol('p'));
      const apply = $(`#FacetsApply-${this.sid}`, this);
      apply?.setAttribute('aria-busy', 'true');
      try {
        const doc = await fetchDoc(query, this.sid);
        if (token !== this.previewToken) return;
        const state = this.captureState();
        this.swap(doc, [`#FacetsDrawerBody-${this.sid}`, `#FacetsApply-${this.sid}`]);
        this.restoreState(state);
        this.announce($(`#FacetsApply-${this.sid}`, this)?.textContent);
      } catch { /* keep current state */ }
      finally { $(`#FacetsApply-${this.sid}`, this)?.removeAttribute('aria-busy'); }
    }

    async resetDrawer() {
      this.drawerDirty = false;
      try {
        const doc = await fetchDoc(this.query, this.sid);
        this.swap(doc, [`#FacetsDrawerBody-${this.sid}`, `#FacetsApply-${this.sid}`]);
      } catch { /* noop */ }
    }

    swap(doc, selectors) {
      selectors.forEach((sel) => {
        const cur = $(sel, this);
        const next = $(sel, doc);
        if (cur && next) cur.innerHTML = next.innerHTML;
      });
    }

    captureState() {
      const active = document.activeElement;
      return {
        focusId: active && this.contains(active) ? active.id : null,
        open: $$('details[open]', this).map((d) => d.id).filter(Boolean),
        expanded: $$('.facet.is-expanded', this).map((d) => d.id).filter(Boolean),
        drawerScroll: $(`#FacetsDrawerBody-${this.sid}`, this)?.scrollTop || 0,
      };
    }

    restoreState({ focusId, open, expanded, drawerScroll }) {
      open.forEach((id) => { const d = document.getElementById(id); if (d) d.open = true; });
      expanded.forEach((id) => {
        const d = document.getElementById(id);
        const btn = d && $('[data-facet-more]', d);
        if (!d || !btn) return;
        d.classList.add('is-expanded');
        btn.setAttribute('aria-expanded', 'true');
        btn.textContent = btn.dataset.labelLess;
      });
      const body = $(`#FacetsDrawerBody-${this.sid}`, this);
      if (body) body.scrollTop = drawerScroll;
      if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
    }

    announce(text) {
      const status = $(`#FacetsStatus-${this.sid}`, this);
      if (!status) return;
      const msg = text || $(`#FacetsCount-${this.sid}`, this)?.textContent || '';
      status.textContent = '';
      requestAnimationFrame(() => { status.textContent = msg.trim(); });
    }

    /* ---- density ---- */
    initDensity() {
      this.group = $('[data-density-group]', this);
      if (!this.group) return;
      this.group.hidden = false;
      this.mq = matchMedia('(min-width: 990px)');
      this.onMq = () => this.syncDensity();
      this.mq.addEventListener('change', this.onMq);
      let saved = SIM.store ? SIM.store.get('density') : '';
      if (saved !== 'large' && saved !== 'small') saved = '';
      this.setDensity(saved, false);
    }

    defaultDensity() {
      if (this.mq?.matches) return this.dataset.colsDesktop === '3' ? 'large' : 'small';
      return this.dataset.colsMobile === '1' ? 'large' : 'small';
    }

    setDensity(value, persist) {
      if (value) this.dataset.density = value;
      else delete this.dataset.density;
      if (persist && SIM.store) SIM.store.set('density', value);
      this.syncDensity();
      this.applySizes();
    }

    syncDensity() {
      const current = this.dataset.density || this.defaultDensity();
      $$('[data-density-value]', this).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.densityValue === current)));
    }

    applySizes() {
      const d = this.dataset.density;
      const list = $(`#ProductGridList-${this.sid}`, this);
      if (!d || !list) return;
      const sizes = d === 'large' ? list.dataset.sizesLarge : list.dataset.sizesSmall;
      if (sizes) $$('.card__img', list).forEach((img) => { if (img.sizes !== sizes) img.sizes = sizes; });
    }

    /* ---- sticky toolbar state (adds a hairline once stuck) ---- */
    observeToolbar() {
      const bar = $('[data-facets-toolbar]', this);
      if (!bar || !('IntersectionObserver' in window)) return;
      const sentinel = document.createElement('span');
      sentinel.className = 'facets__sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      bar.before(sentinel);
      new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-stuck', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0 }).observe(sentinel);
    }
  }
  customElements.define('collection-facets', CollectionFacets);
}

/* --------------------------------------------------------------------------
   <load-more> — intercepts the "Show more" link; appends next page's cards
   -------------------------------------------------------------------------- */
if (!customElements.get('load-more')) {
  class LoadMore extends HTMLElement {
    connectedCallback() {
      this.classList.add('is-enhanced');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-load-more-btn]');
        if (!btn || e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        this.load(btn);
      });
    }

    async load(btn) {
      if (btn.getAttribute('aria-busy') === 'true') return;
      const sid = this.dataset.sectionId;
      const list = document.getElementById(`ProductGridList-${sid}`);
      if (!list) { location.href = btn.href; return; }
      btn.setAttribute('aria-busy', 'true');
      try {
        const doc = await fetchDoc(new URL(btn.href, location.origin).search.slice(1), sid);
        const items = $$(`#ProductGridList-${sid} > [data-grid-item]:not([data-tile])`, doc);
        const nodes = items.map((n) => document.importNode(n, true));
        const host = this.closest('collection-facets');
        const density = host?.dataset.density;
        if (density) {
          const sizes = density === 'large' ? list.dataset.sizesLarge : list.dataset.sizesSmall;
          nodes.forEach((n) => $$('.card__img', n).forEach((img) => (img.sizes = sizes)));
        }
        list.append(...nodes);
        const next = $(`#ProductGrid-${sid} load-more`, doc);
        if (next) this.innerHTML = next.innerHTML;
        this.classList.add('plp-more--js-only');
        reveal(list);
        const status = $('[data-load-more-status]', this);
        const live = document.getElementById(`FacetsStatus-${sid}`);
        if (live && status) live.textContent = status.textContent.trim();
        const firstLink = nodes[0] && $('a[href]', nodes[0]);
        firstLink?.focus({ preventScroll: true });
      } catch {
        location.href = btn.href;
      } finally {
        btn.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('load-more', LoadMore);
}

/* --------------------------------------------------------------------------
   <read-more> — 3-line clamp (mobile) with disclosure button when it overflows
   -------------------------------------------------------------------------- */
if (!customElements.get('read-more')) {
  class ReadMore extends HTMLElement {
    connectedCallback() {
      this.body = $('[data-read-more-body]', this);
      this.btn = $('button', this);
      if (!this.body || !this.btn) return;
      this.classList.add('is-clamped');
      const check = () => {
        if (this.hasAttribute('expanded')) return;
        this.btn.hidden = this.body.scrollHeight <= this.body.clientHeight + 2;
      };
      new ResizeObserver(check).observe(this.body);
      this.btn.addEventListener('click', () => {
        const open = this.toggleAttribute('expanded');
        this.classList.toggle('is-clamped', !open);
        this.btn.setAttribute('aria-expanded', String(open));
        this.btn.textContent = open ? this.btn.dataset.labelLess : this.btn.dataset.labelMore;
      });
    }
  }
  customElements.define('read-more', ReadMore);
}
